import { uid as getUid } from "@dhx/ts-common/core";
import { sv, VNode } from "@dhx/ts-common/dom";
import { getFontStyle, getTextWidth, verticalCenteredText } from "../helpers/common";
import { IGridRenderConfig, IScaleConfig } from "../types";

const defaultTextTemplate = <T>(item: T): string => item.toString();

type PointInfo = [number, string];

export function bottom(points: PointInfo[], config: IScaleConfig, width: number, height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText } = config;
	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		extraTittlePadding = textPadding;
		text = points.map(p => p[0] !== 0 ? sv("text", {class: "scale-text top-text", x: p[0], y: height + textPadding}, [verticalCenteredText(template(p[1]))])
			: sv("text", {class: "scale-text start-text top-text", x: p[0], y: height + textPadding}, [verticalCenteredText(template(p[1]))])
		);
	}
	const id = getUid();
	let svTitle = null;
	const mainLine = sv("path", { class: "main-scale", d:`M0 ${height} H${width - 0.5}`, id}); // -0.5 instead of 0, coz stroke-linecap: square and dirrent stroke size
	if (title) {
		svTitle = sv("text", {dx: width / 2, dy: scalePadding + extraTittlePadding}, [
			sv("textPath", {href: `#${id}`, class: "scale-title"}, title)
		]);
	}
	return sv("g", [
		mainLine,
		svTitle
	].concat(text));
}
export function bottomGrid(points: PointInfo[], width: number, height: number, config: IGridRenderConfig): VNode {
	const {dashed, grid, targetLine, targetValue} = config;
	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line": ""}`;
	for (let i=0; i<len; i++) {
		if (i === 0 && points[i][0] === 0 && !config.hidden) {
			continue;
		}
		if (i === targetLine) {
			const d = `M${points[i][0]} 0 V ${height}`;
			const path = sv("path", {d, class: `${className} spec-grid-line`});
			gridLines.push(path);
			continue;
		}
		if (grid) {
			const d = `M${points[i][0]} 0 V ${height}`;
			const path = sv("path", {d, class: className});
			gridLines.push(path);
			if (i === len - 1 && points[i][0] !== width) {
				const additionD = `M${width} 0 V ${height}`;
				const additionPath = sv("path", {d: additionD, class:className});
				gridLines.push(additionPath);
			}
		}
	}
	if (targetValue) {
		const d = `M${targetValue * width} 0 V ${height}`;
		const path = sv("path", {d, class: `${className} spec-grid-line`});
		gridLines.push(path);
	}
	return sv("g", gridLines);
}
export function top(points: PointInfo[], config: IScaleConfig, width: number, _height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText } = config;

	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		extraTittlePadding = textPadding;
		text = points.map(p => p[0] !== 0 ? sv("text", {x: p[0], y: - textPadding, class: "scale-text"}, [verticalCenteredText(template(p[1]))])
			: sv("text", {x: p[0], y: - textPadding, class: "scale-text start-text"}, [verticalCenteredText(template(p[1]))]));
	}
	const id = getUid();
	const mainLine = sv("path", {d:`M0 0 H${width}`, class: "main-scale", id });
	let svTitle = null;
	if (title) {
		svTitle = sv("text", {dx: width / 2, dy: -scalePadding - extraTittlePadding}, [
			sv("textPath", {href: `#${id}`, class: "scale-title"}, title)
		]);
	}
	return sv("g", [
		mainLine,
		svTitle
	].concat(text));
}
export function topGrid(points: PointInfo[], _width: number, height: number, config: IGridRenderConfig): VNode {
	const {dashed, grid, targetLine} = config;
	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line": ""}`;
	for (let i=0; i<len; i++) {
		if (i === 0 && points[i][0] === 0 && !config.hidden) {
			continue;
		}
		if (i === targetLine) {
			const d = `M${points[i][0]} 0 V ${height}`;
			const path = sv("path", {d, class: `${className} spec-grid-line`});
			gridLines.push(path);
			continue;
		}
		if (grid) {
			const d = `M${points[i][0]} 0 V ${height}`;
			const path = sv("path", {d, class: className});
			gridLines.push(path);
			if (i === len - 1 && points[i][0] !== 0) {
				const additionD = `M0 0 V ${height}`;
				const additionPath = sv("path", {d: additionD, class:className});
				gridLines.push(additionPath);
			}
		}
	}
	return sv("g", gridLines);
}
export function left(points: PointInfo[], config: IScaleConfig, _width: number, height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText } = config;
	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		const style = getFontStyle("scale-text");
		let maxTextWidth = 0;
		text = points.map(p => {
			const scaleText = template(p[1]);
			if (title) {
				const textWidth = getTextWidth(scaleText, style);
				if (maxTextWidth < textWidth) {
					maxTextWidth = textWidth;
				}
			}
			return sv("text", {class: "scale-text end-text", x: -textPadding, y: p[0]}, [verticalCenteredText(scaleText)]);
		});
		extraTittlePadding = maxTextWidth + textPadding;
	}
	const id = getUid();
	const mainLine = sv("path", {class: "main-scale", d:`M0 ${height} V 0.5`, id}); // 0.5 instead of 0, coz stroke-linecap: square and dirrent stroke size
	let svTitle = null;
	if (title) {
		svTitle = sv("text", {dx: height / 2, dy: -scalePadding - extraTittlePadding}, [
			sv("textPath", {href: `#${id}`, class: "scale-title"}, title)
		]);
	}
	return sv("g", [
		mainLine,
		svTitle
	].concat(text));
}
export function leftGrid(points: PointInfo[], width: number, height: number, config: IGridRenderConfig): VNode {
	const {dashed, grid, targetLine, targetValue} = config;
	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line": ""}`;
	for (let i=0; i<len; i++) {
		if (i === 0 && points[i][0] === height && !config.hidden) {
			continue;
		}
		if (targetLine === i) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", {d, class: `${className} spec-grid-line`});
			gridLines.push(path);
			continue;
		}
		if (grid) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", {d, class: className});
			gridLines.push(path);
			if (i === len - 1 && points[i][0] !== width) {
				const additionD = `M0 0 H${width}`;
				const additionPath = sv("path", {d: additionD, class: className});
				gridLines.push(additionPath);
			}
		}
	}
	if (targetValue) {
		const d = `M0 ${targetValue * height} H ${width}`;
		const path = sv("path", {d, class: `${className} spec-grid-line`});
		gridLines.push(path);
	}
	return sv("g", gridLines);
}
export function right(points: PointInfo[], config: IScaleConfig, width: number, height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText } = config;

	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		const style = getFontStyle("scale-text");
		let maxTextWidth = 0;
		text = points.map(p => {
			const scaleText = template(p[1]);
			if (title) {
				const textWidth = getTextWidth(scaleText, style);
				if (maxTextWidth < textWidth) {
					maxTextWidth = textWidth;
				}
			}
			return sv("text", {x: width + textPadding, class: "scale-text start-text", y: p[0]}, [verticalCenteredText(scaleText)]);
		});
		extraTittlePadding = textPadding + maxTextWidth;
	}
	const id = getUid();
	const mainLine = sv("path", { d:`M${width} ${height} V 0`, class: "main-scale", id});
	let svTitle = null;
	if (title) {
		svTitle = sv("text", {dx: height / 2, dy: scalePadding + extraTittlePadding}, [
			sv("textPath", {href: `#${id}`, class: "scale-title"}, title)
		]);
	}
	return sv("g", [
		mainLine,
		svTitle
	].concat(text));
}
export function rightGrid(points: PointInfo[], width: number, height: number, config: IGridRenderConfig): VNode {
	const {dashed, grid, targetLine} = config;
	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line": ""}`;
	for (let i=0; i<len; i++) {
		if (i === 0 && points[i][0] === height && !config.hidden) {
			continue;
		}
		if (targetLine === i) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", {d, class: `${className} spec-grid-line`});
			gridLines.push(path);
			continue;
		}
		if (grid) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", {d, class: className});
			gridLines.push(path);
			if (i === len - 1 && points[i][0] !== width) {
				const additionD = `M0 0 H${width}`;
				const additionPath = sv("path", {d: additionD, class:className});
				gridLines.push(additionPath);
			}
		}
	}
	return sv("g", gridLines);
}